import { AdminVideo } from '../model/dto/video/admin-video';
import { VideoStatus } from '../model/dto/video/video-constants';
import { StatusTone } from '../model/type/status-tone.type';
import { VideoStatusDisplay } from '../model/type/video-status-display';
import { UploadJobPhase } from '../model/type/upload-job-phase.type';
import { VideoUploadItem } from '../../context/video-upload.types';
import { uploadPercent } from '../../context/video-upload-queue';

const STATUS_TONES: Record<VideoStatus, StatusTone> = {
  uploading: 'initial',
  processing: 'progress',
  ready: 'success',
  failed: 'failure',
};

export const getVideoStatusDisplay = (
  video: AdminVideo,
  upload?: VideoUploadItem
): VideoStatusDisplay => {
  if (video.status !== 'uploading') {
    return {
      kind: 'status',
      status: video.status,
      tone: STATUS_TONES[video.status],
    };
  }

  if (!upload) {
    return { kind: 'uploadNotFinished', tone: 'caution' };
  }

  switch (upload.phase) {
    case 'queued':
      return { kind: 'queued' };

    case 'uploading':
      return { kind: 'uploading', percent: uploadPercent(upload) };

    case 'completing':
    case 'finished':
      return { kind: 'finishing' };

    case 'failed':
      return { kind: 'uploadFailed', tone: 'failure' };

    default: {
      const unhandledPhase: never = upload.phase;

      return unhandledPhase;
    }
  }
};

export const isArchived = (video: AdminVideo): boolean => !!video.archived_at;

export const isAwaitingChange = (video: AdminVideo): boolean =>
  video.status === 'processing' || !!video.replaced_by_video_id;

export const hasChanged = (before: AdminVideo, after: AdminVideo): boolean =>
  before.status !== after.status ||
  before.replaced_by_video_id !== after.replaced_by_video_id;

export const isSwap = (before: AdminVideo, after: AdminVideo): boolean =>
  before.replaced_by_video_id !== after.replaced_by_video_id ||
  (after.status === 'ready' && !!after.replaces_video_id);

export const getUploadJobPhase = (
  display: VideoStatusDisplay
): UploadJobPhase => {
  switch (display.kind) {
    case 'queued':
    case 'uploading':
    case 'finishing':
    case 'uploadFailed':
      return display.kind;

    case 'uploadNotFinished':
      return 'uploadFailed';

    case 'status':
      switch (display.status) {
        case 'uploading':
          return 'finishing';

        case 'processing':
          return 'processing';

        case 'ready':
          return 'ready';

        case 'failed':
          return 'processingFailed';

        default: {
          const unhandledStatus: never = display.status;

          return unhandledStatus;
        }
      }

    default: {
      const unhandledDisplay: never = display;

      return unhandledDisplay;
    }
  }
};
